import { useEffect, useRef, useState } from "react";
import { uploadMedia, deleteMedia, updateMediaCaption, mediaUrl, fetchUploadSettings } from "@/villa142/api";

const FALLBACK = { image_max_bytes: 10 * 1024 * 1024, image_budget_bytes: 30 * 1024 * 1024, image_max_mb: 10, image_budget_mb: 30 };
const mb = (b) => `${Math.round((b / (1024 * 1024)) * 10) / 10}`.replace(/\.0$/, "");
const isImageFile = (file) => file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name || "");

export const UploadTray = ({ cfg, contributor, media, ops, itemId = "", compact = false }) => {
  const images = (media || []).filter((m) => m.kind === "image" && (m.item_id || "") === itemId);
  const inputRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [limits, setLimits] = useState(FALLBACK);
  const pendingRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchUploadSettings().then((s) => { if (alive && s) setLimits(s); });
    return () => { alive = false; };
  }, []);

  const usedBytes = (media || []).filter((m) => m.kind === "image").reduce((s, m) => s + (m.bytes || 0), 0);

  const doUpload = async (file) => {
    setStatus("uploading");
    setMessage("");
    pendingRef.current = file;
    try {
      const item = await uploadMedia(cfg.key, contributor, "image", file, file.name, "", itemId);
      ops.add(cfg.key, contributor, item);
      pendingRef.current = null;
      setStatus("idle");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 400) {
        pendingRef.current = null;
        setStatus("error");
        setMessage(typeof detail === "string" ? detail : "That file could not be accepted — please try a different photo.");
      } else {
        setStatus("error");
        setMessage("The image is kept here — we will try saving it again.");
      }
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isImageFile(file)) {
      setStatus("error");
      setMessage("Please choose a photo — JPEG, PNG, HEIC, WebP or GIF.");
      return;
    }
    if (file.size > limits.image_max_bytes) {
      setStatus("error");
      setMessage(`That image is a little too large — please keep each image under ${mb(limits.image_max_bytes)} MB.`);
      return;
    }
    if (usedBytes + file.size > limits.image_budget_bytes) {
      setStatus("error");
      setMessage(`These images add up to more than the ${mb(limits.image_budget_bytes)} MB we can keep for one response — please remove one or use a smaller photo.`);
      return;
    }
    doUpload(file);
  };

  const [confirmId, setConfirmId] = useState(null);
  const remove = async (id) => {
    setConfirmId(null);
    try {
      await deleteMedia(id);
      ops.remove(cfg.key, contributor, id);
    } catch {}
  };

  const captionTimers = useRef({});
  const onCaption = (id, caption) => {
    ops.caption(cfg.key, contributor, id, caption);
    clearTimeout(captionTimers.current[id]);
    captionTimers.current[id] = setTimeout(() => updateMediaCaption(id, caption), 800);
  };

  const testSuffix = itemId ? `${cfg.key}-${itemId}` : cfg.key;

  return (
    <div className={`v142-tray-wrap${compact ? " is-compact" : ""}`}>
      {cfg.label && (
        <div className="v142-field-head">
          <span className="v142-label-follow">{cfg.label}</span>
          {cfg.support && <p className="v142-support">{cfg.support}</p>}
        </div>
      )}
      <div className="v142-tray" data-testid={`v142-tray-${testSuffix}`}>
        {images.map((img) => (
          <figure className="v142-tray-item" key={img.id} data-testid={`v142-tray-item-${img.id}`}>
            <img
              src={mediaUrl(img.id, "", "thumb")}
              srcSet={`${mediaUrl(img.id, "", "thumb")} 600w, ${mediaUrl(img.id, "", "display")} 1600w`}
              sizes="(max-width: 640px) 45vw, 220px"
              alt={img.caption || "Shared reference"}
              className="v142-tray-img"
              loading="lazy"
            />
            <input
              type="text"
              className="v142-tray-caption"
              aria-label={cfg.captionPrompt}
              placeholder={cfg.captionPrompt}
              value={img.caption || ""}
              onChange={(e) => onCaption(img.id, e.target.value)}
              data-testid={`v142-caption-${img.id}`}
            />
            {confirmId === img.id ? (
              <div className="v142-note-confirm" data-testid={`v142-remove-confirm-${img.id}`}>
                <p className="v142-recorder-note">Remove this photo? This cannot be undone.</p>
                <div className="v142-recorder-actions">
                  <button type="button" className="v142-link" data-testid={`v142-remove-cancel-${img.id}`} onClick={() => setConfirmId(null)}>Keep</button>
                  <button type="button" className="v142-link v142-danger-link" data-testid={`v142-remove-confirm-yes-${img.id}`} onClick={() => remove(img.id)}>Remove</button>
                </div>
              </div>
            ) : (
              <button type="button" className="v142-link v142-tray-remove" data-testid={`v142-remove-${img.id}`} onClick={() => setConfirmId(img.id)}>
                Remove
              </button>
            )}
          </figure>
        ))}
        {images.length < cfg.max && (
          <button
            type="button"
            className="v142-tray-add"
            data-testid={`v142-add-image-${testSuffix}`}
            onClick={() => inputRef.current?.click()}
            disabled={status === "uploading"}
          >
            <span className="v142-tray-add-plus" aria-hidden="true">+</span>
            {status === "uploading" ? "Adding…" : "Add a photo"}
            <span className="v142-tray-add-note">Up to {cfg.max}</span>
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*,.heic,.heif" className="v142-visually-hidden" onChange={onFile} aria-label={cfg.label || "Add a photo"} />
      </div>
      {status === "error" && (
        <div className="v142-tray-error">
          <span className="v142-recorder-note" role="alert">{message}</span>
          {pendingRef.current && (
            <button type="button" className="v142-btn v142-btn-small" data-testid={`v142-retry-image-${testSuffix}`} onClick={() => doUpload(pendingRef.current)}>
              Try again
            </button>
          )}
        </div>
      )}
    </div>
  );
};
